<script lang="ts" setup>
import { useUserStore } from '@/store/user';

const LIGHTEN = 90;
const COLORS: string[] = [
	'#F44336',
	'#FF4081',
	'#9C27B0',
	'#673AB7',
	'#3F51B5',
	'#2196F3',
	'#03A9F4',
	'#00BCD4',
	'#4CAF50',
	'#8BC34A',
	'#CDDC39',
	'#FFEB3B',
	'#FFC107',
	'#FF9800',
	'#FF5722',
];

const props = defineProps({
	size: {
		type: Number,
		default: 42,
	},
});
const emit = defineEmits(['click']);

const userStore = useUserStore();
const config = useRuntimeConfig();
const username = computed(() => {
	const _username = userStore.getUserInfo ? userStore.getUserInfo.email : '';
	if (_username?.includes('@')) {
		return _username.split('@')[0];
	}
	return _username + '';
});

const isLoggedIn = computed(() => userStore.getLoggedIn);
const userAvatar = computed(() => userStore.getUserAvatar);

const imageUrl = computed(() => {
	if (isLoggedIn.value) {
		return userAvatar.value || '';
	}
	return `${config.VUE_APP_BASE_URL}images/avatar.png`;
});

const randomBackgroundColor = (seed: number) => {
	return COLORS[seed % COLORS.length];
};

const lightenColor = (hex: string, amt: number) => {
	// From https://css-tricks.com/snippets/javascript/lighten-darken-color/
	let usePound = false;
	if (hex[0] === '#') {
		hex = hex.slice(1);
		usePound = true;
	}
	const num = parseInt(hex, 16);
	let r = (num >> 16) + amt;
	if (r > 255) {
		r = 255;
	} else if (r < 0) {
		r = 0;
	}

	let b = ((num >> 8) & 0x00ff) + amt;
	if (b > 255) {
		b = 255;
	} else if (b < 0) {
		b = 0;
	}

	let g = (num & 0x0000ff) + amt;
	if (g > 255) {
		g = 255;
	} else if (g < 0) {
		g = 0;
	}
	return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
};

const getInitials = (username: string) => {
	let parts = username.split(/[ -]/);
	let initials = '';
	for (let i = 0; i < parts.length; i++) {
		initials += parts[i].charAt(0);
	}
	if (initials.length > 3 && initials.search(/[A-Z]/) !== -1) {
		initials = initials.replace(/[a-z]+/g, '');
	}
	initials = initials.substr(0, 3).toUpperCase();
	return initials;
};

const bgColor = computed(() => {
	return randomBackgroundColor(username.value.length);
});

const fontColor = computed(() => {
	return lightenColor(bgColor.value, LIGHTEN);
});

const userInitial = computed(() => {
	return getInitials(username.value) || 'A';
});

const style = computed(() => {
	let _style = {
		display: 'flex',
		flexShrink: 0,
		justifyContent: 'center',
		alignItems: 'center',
		borderRadius: '50%',
		backgroundColor: bgColor.value,
		width: props.size + 'px',
		height: props.size + 'px',
		color: fontColor.value,
		font: `${Math.floor(props.size / 2.5)}px/${props.size}px Helvetica, Arial, sans-serif`,
	};
	if (imageUrl.value) {
		_style = {
			..._style,
			...{
				background: `transparent url('${imageUrl.value}') no-repeat scroll 0% 0% / ${props.size}px ${props.size}px content-box border-box`,
			},
		};
	}
	return _style;
});

const handleClick = () => {
	emit('click');
};
</script>

<template>
	<div
		id="user-avatar"
		class="user-avatar-letter--wrapper cursor-pointer"
		:style="[style]"
		aria-hidden="true"
		@click="handleClick"
	>
		<div v-if="!imageUrl">
			{{ userInitial }}
		</div>
	</div>
</template>
